import { useRef, useState, useLayoutEffect, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Box, Icon, Text } from '@quintoandar-tokko/jopijs';
import ParticipantCard from '@/components/ParticipantCard';
import styles from './styles.module.scss';
import useOnClickOutside from '@/components/shared/Hooks';
import useTrackPerformanceTable from '@/components/Dashboard/customHooks/useTrackDashboard';
import { TRACK_ELEMENT } from '@/components/Dashboard/constants/track';

export default function ContactCell({
  highlighted = true,
  id,
  image,
  name,
  email,
  cellphone,
  isValidCellphone,
  leadStatus,
  agent,
  relativeSelector = '',
  modal,
  ...rest
}) {
  const [isCardOpen, setIsCardOpen] = useState(false);
  const [cardPosition, setCardPosition] = useState(null);
  const [modalEl, setModalEl] = useState(null);
  const cellRef = useRef(null);
  const cardRef = useRef(null);
  const track = useTrackPerformanceTable();
  useOnClickOutside(cellRef, isCardOpen, setIsCardOpen);

  useEffect(() => {
    if (!relativeSelector) return;

    const el = document.querySelector(relativeSelector);
    setModalEl(el);
  }, []);

  useLayoutEffect(() => {
    if (!isCardOpen || !cellRef.current || !modalEl) return;

    const cellRect = cellRef.current.getBoundingClientRect();
    const modalRect = modalEl.getBoundingClientRect();

    const top = cellRect.top - modalRect.top;
    const left = cellRect.left - modalRect.left;

    setCardPosition({ top, left });
  }, [isCardOpen, modalEl]);

  const shouldShowCard = isCardOpen && cardPosition && modalEl;
  const thumbnail = image ? (
    <Box as="img" className={styles.participantImage} src={image} />
  ) : (
    <Icon icon="icon-contactos" className={styles.contactCellIcon} />
  );
  const participant = {
    id,
    name,
    email,
    cellphone,
    profilePicture: image,
    leadStatus: leadStatus?.name,
    leadStatusColor: leadStatus?.color,
    agentName: agent?.name,
    agentId: agent?.id,
    // Prefer an explicit API flag (assignment report). Dashboard rows still
    // fall back to "any cellphone means WhatsApp".
    isValidCellphone:
      isValidCellphone == null ? Boolean(cellphone) : Boolean(isValidCellphone),
  };

  if (!id)
    return <Icon icon="icon-mostrarmenos" className={styles.emptyDataIcon} />;

  const toggleOpenCard = () => {
    setIsCardOpen((prev) => !prev);
    if (track[modal]) track[modal](TRACK_ELEMENT.CONTACT_NAME);
  };

  return (
    <>
      <Box
        ref={cellRef}
        className={styles.contactCell}
        onClick={toggleOpenCard}
        {...rest}
      >
        {highlighted && (
          <Box className={styles.participantImageWrapper}>{thumbnail}</Box>
        )}
        <Text className={styles.contactCellText} data-highlighted={highlighted}>
          {name}
        </Text>
      </Box>

      {shouldShowCard &&
        createPortal(
          <Box
            ref={cardRef}
            className={styles.pendingCardContainer}
            __css={{ left: cardPosition.left, top: cardPosition.top }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <ParticipantCard type="contact" data={participant} />
          </Box>,
          modalEl
        )}
    </>
  );
}
